import { memo, useMemo } from "react"
import { linkOptions, useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"

import {
	defaultChangelogCoverUrl,
	generatedCoverVersion,
	isChangelogCoverHidden,
} from "@productlane/lib/src/changelog-cover"
import { cn } from "@productlane/lib/src/cn"
import { ChangelogTimelineDot, ChangelogTimelineLine } from "@productlane/ui"

import type { OverviewChangelog } from "./changelog-overview-model"
import { env } from "@/env"
import { useSelectedLanguage } from "@/screens/settings/settings-pages/language-selector"
import { MOBILE_BAR_CLEARANCE } from "../sidebar-geometry"
import {
	buildChangelogOverview,
	changelogEditedLabel,
	changelogExcerpt,
	changelogTimestamp,
	localizedChangelog,
} from "./changelog-overview-model"
import { useRetryingCoverSrc } from "./generated-cover-button"

const openPost = (id: string) =>
	linkOptions({ to: "/edit-changelog/$id", params: { id } })

// The same tray the docs home puts around its AI chat field.
const DRAFT_TRAY = [
	"bg-black/[0.02] ring-1 ring-black/[0.09]",
	"dark:bg-black/25 dark:shadow-[inset_0_1px_3px_rgba(0,0,0,0.5)] dark:ring-white/[0.05]",
]

// Older posts carry their year; this year's read cleaner without it.
const postDate = (changelog: OverviewChangelog, now = new Date()) => {
	const date = new Date(changelogTimestamp(changelog))
	return format(
		date,
		date.getFullYear() === now.getFullYear() ? "MMM d" : "MMM d, yyyy",
	)
}

export const ChangelogOverview = memo(
	({ changelogs }: { changelogs: readonly OverviewChangelog[] }) => {
		const { drafts, latest, older } = useMemo(
			() => buildChangelogOverview(changelogs),
			[changelogs],
		)
		const hasPublished = latest.length > 0

		return (
			<div
				className={cn(
					"mx-auto flex w-full max-w-4xl flex-col gap-10 px-4 pb-16 pt-8 sm:px-8",
					MOBILE_BAR_CLEARANCE,
				)}
			>
				{drafts.length > 0 && (
					<section className="flex flex-col gap-3" aria-label="Drafts">
						<SectionHeading label="Drafts" count={drafts.length} />
						<DraftStrip drafts={drafts} />
					</section>
				)}

				{hasPublished && (
					<section className="flex flex-col" aria-label="Published">
						<SectionHeading label="Published" className="mb-5" />
						<ol className="flex list-none flex-col">
							{latest.map((changelog, index) => (
								<LatestRow
									key={changelog.id}
									changelog={changelog}
									isFirst={index === 0}
									isLast={index === latest.length - 1 && older.length === 0}
								/>
							))}
							{older.map((changelog, index) => (
								<CompactRow
									key={changelog.id}
									changelog={changelog}
									isLast={index === older.length - 1}
								/>
							))}
						</ol>
					</section>
				)}
			</div>
		)
	},
)
ChangelogOverview.displayName = "ChangelogOverview"

const DraftStrip = ({ drafts }: { drafts: readonly OverviewChangelog[] }) => (
	<div className={cn("overflow-hidden rounded-[14px]", DRAFT_TRAY)}>
		<ul className="no-scrollbar flex list-none gap-3 overflow-x-auto p-2">
			{drafts.map((changelog) => (
				<DraftCard key={changelog.id} changelog={changelog} />
			))}
		</ul>
	</div>
)

const SectionHeading = ({
	label,
	count,
	className,
}: {
	label: string
	count?: number
	className?: string
}) => (
	<h2
		className={cn(
			"flex items-baseline gap-2 text-sm font-semibold text-primary",
			className,
		)}
	>
		{label}
		{count !== undefined && (
			<span className="text-sm font-normal text-tertiary">{count}</span>
		)}
	</h2>
)

const PUBLISHED_DOT_COLOR = "#9FA7B7"

// The portal's own dot and line, one segment per row so the line runs unbroken.
// dotTop puts the dot's centre on the row title's first line.
const Rail = ({
	dotTop,
	isFirst,
	isLast,
}: {
	dotTop: number
	isFirst: boolean
	isLast: boolean
}) => (
	<span aria-hidden className="relative w-4 flex-none">
		<ChangelogTimelineLine
			isLast={isLast}
			className="left-1/2"
			style={{ top: isFirst ? dotTop + 7 : 0 }}
		/>
		<ChangelogTimelineDot
			className="left-1/2"
			style={{ top: dotTop }}
			color={PUBLISHED_DOT_COLOR}
		/>
	</span>
)

const Cover = ({
	src,
	className,
}: {
	src: string | null
	className: string
}) => {
	const { imgSrc, onError } = useRetryingCoverSrc(src ?? "")
	return (
		<span
			className={cn(
				"block flex-none overflow-hidden bg-gray-100 ring-1 ring-black/10 dark:bg-gray-800 dark:ring-white/10",
				className,
			)}
		>
			{src && imgSrc && (
				<img
					src={imgSrc}
					alt=""
					loading="lazy"
					decoding="async"
					className="block h-full w-full object-cover"
					onError={onError}
				/>
			)}
		</span>
	)
}

const useLocalized = (changelog: OverviewChangelog) => {
	const [selectedLanguage] = useSelectedLanguage()
	return useMemo(() => {
		const localized = localizedChangelog(changelog, selectedLanguage.code)
		return {
			...localized,
			imageUrl: isChangelogCoverHidden(localized.imageUrl)
				? null
				: localized.imageUrl ||
					`${defaultChangelogCoverUrl(env.VITE_API_URL, changelog.id, selectedLanguage.code)}?v=${generatedCoverVersion(changelog.date ?? changelog.createdAt, localized.title)}`,
		}
	}, [changelog, selectedLanguage.code])
}

const DraftCard = memo(({ changelog }: { changelog: OverviewChangelog }) => {
	const navigate = useNavigate()
	const { title, imageUrl } = useLocalized(changelog)

	return (
		<li className="w-48 flex-none">
			<button
				type="button"
				onClick={() => void navigate(openPost(changelog.id))}
				className="flex w-full flex-col gap-2 rounded-lg p-1 text-left hover:bg-gray-100 dark:hover:bg-gray-800/50"
			>
				<Cover src={imageUrl} className="aspect-[16/9] w-full rounded-md" />
				<span className="flex min-w-0 flex-col gap-0.5 px-0.5">
					<span className="truncate text-sm font-medium text-primary">
						{title}
					</span>
					<span className="truncate text-xs text-tertiary">
						{changelogEditedLabel(changelog)}
					</span>
				</span>
			</button>
		</li>
	)
})
DraftCard.displayName = "DraftCard"

const LatestRow = memo(
	({
		changelog,
		isFirst,
		isLast,
	}: {
		changelog: OverviewChangelog
		isFirst: boolean
		isLast: boolean
	}) => {
		const navigate = useNavigate()
		const { title, notes, imageUrl } = useLocalized(changelog)
		const excerpt = useMemo(() => changelogExcerpt(notes), [notes])

		return (
			<li className="flex gap-3">
				<span className="w-20 flex-none pt-3 text-right text-xs text-tertiary">
					{postDate(changelog)}
				</span>
				<Rail dotTop={13} isFirst={isFirst} isLast={isLast} />
				<div className="min-w-0 flex-1 pb-6">
					<button
						type="button"
						onClick={() => void navigate(openPost(changelog.id))}
						className="flex w-full flex-col gap-4 rounded-xl p-2 text-left hover:bg-gray-50 sm:flex-row sm:items-start sm:gap-6 dark:hover:bg-gray-800/50"
					>
						<Cover
							src={imageUrl}
							className="aspect-[16/9] w-full rounded-lg sm:w-60"
						/>
						<span className="flex min-w-0 flex-1 flex-col gap-1.5">
							<span className="text-base font-semibold leading-6 tracking-tight text-primary">
								{title}
							</span>
							{excerpt && (
								<span className="line-clamp-3 text-sm leading-relaxed text-secondary">
									{excerpt}
								</span>
							)}
						</span>
					</button>
				</div>
			</li>
		)
	},
)
LatestRow.displayName = "LatestRow"

const CompactRow = memo(
	({
		changelog,
		isLast,
	}: {
		changelog: OverviewChangelog
		isLast: boolean
	}) => {
		const navigate = useNavigate()
		const { title, imageUrl } = useLocalized(changelog)

		return (
			<li className="flex gap-3">
				<span className="w-20 flex-none pt-[17px] text-right text-xs text-tertiary">
					{postDate(changelog)}
				</span>
				<Rail dotTop={19} isFirst={false} isLast={isLast} />
				<div className="min-w-0 flex-1 py-0.5">
					<button
						type="button"
						onClick={() => void navigate(openPost(changelog.id))}
						className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg px-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50"
					>
						<Cover src={imageUrl} className="aspect-[16/9] w-14 rounded" />
						<span className="min-w-0 flex-1 truncate text-sm font-medium text-primary">
							{title}
						</span>
					</button>
				</div>
			</li>
		)
	},
)
CompactRow.displayName = "CompactRow"
